import { useMemo, useState } from 'react'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { StatusBadge } from '../components/StatusBadge'
import { useApp } from '../context/AppContext'
import type { AppUser, UserPerfil } from '../types'

const perfis: UserPerfil[] = [
  'ADMINISTRADOR',
  'DIRETORIA',
  'ENGENHARIA',
  'SUPRIMENTOS',
  'FINANCEIRO',
  'OPERACIONAL',
]

export default function Usuarios() {
  const {
    users,
    currentUser,
    addLog,
    saveUser,
    setUserStatus,
    deleteUser,
    changeOwnPassword,
    usersLoading,
    usersPersistence,
  } = useApp()
  const [selectedId, setSelectedId] = useState(users[0]?.id ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [passwordMessage, setPasswordMessage] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [form, setForm] = useState({
    nome: '',
    email: '',
    perfil: 'OPERACIONAL' as UserPerfil,
    status: 'ATIVO' as AppUser['status'],
    senha: '',
    confirmarSenha: '',
  })
  const [ownPasswordForm, setOwnPasswordForm] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarNovaSenha: '',
  })
  const [editing, setEditing] = useState<AppUser | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const selectedUser = useMemo(
    () => users.find(user => user.id === selectedId) ?? users[0] ?? null,
    [selectedId, users],
  )

  const ativos = users.filter(user => user.status === 'ATIVO').length
  const admins = users.filter(user => user.perfil === 'ADMINISTRADOR').length
  const paginatedUsers = paginateItems(users, page, pageSize)

  function resetForm() {
    setForm({
      nome: '',
      email: '',
      perfil: 'OPERACIONAL',
      status: 'ATIVO',
      senha: '',
      confirmarSenha: '',
    })
    setEditing(null)
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError('')

    if (!form.nome.trim() || !form.email.trim()) return

    if (!editing && !form.senha.trim()) {
      setError('Informe uma senha inicial para o usuario.')
      return
    }

    if (form.senha || form.confirmarSenha) {
      if (form.senha.trim().length < 6) {
        setError('A senha precisa ter pelo menos 6 caracteres.')
        return
      }

      if (form.senha !== form.confirmarSenha) {
        setError('A confirmacao de senha nao confere.')
        return
      }
    }

    setSaving(true)

    try {
      if (editing) {
        await saveUser({
          ...editing,
          nome: form.nome,
          email: form.email,
          perfil: form.perfil,
          status: form.status,
          senha: form.senha || undefined,
        }, editing.id)
        addLog('USUARIO', editing.id, 'EDITADO', form.senha ? `Usuario ${form.nome} atualizado com nova senha` : `Usuario ${form.nome} atualizado`)
        resetForm()
        return
      }

      const nextUser = await saveUser({
        nome: form.nome,
        email: form.email,
        perfil: form.perfil,
        status: form.status,
        senha: form.senha,
      })

      setSelectedId(nextUser.id)
      addLog('USUARIO', nextUser.id, 'CRIADO', `Usuario ${nextUser.nome} criado`)
      resetForm()
    } catch {
      setError('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  function startEdit(user: AppUser) {
    setSelectedId(user.id)
    setEditing(user)
    setForm({
      nome: user.nome,
      email: user.email,
      perfil: user.perfil,
      status: user.status,
      senha: '',
      confirmarSenha: '',
    })
  }

  async function submitOwnPassword(event: React.FormEvent) {
    event.preventDefault()
    setPasswordError('')
    setPasswordMessage('')

    if (!ownPasswordForm.senhaAtual || !ownPasswordForm.novaSenha) {
      setPasswordError('Informe a senha atual e a nova senha.')
      return
    }

    if (ownPasswordForm.novaSenha.trim().length < 6) {
      setPasswordError('A nova senha precisa ter pelo menos 6 caracteres.')
      return
    }

    if (ownPasswordForm.novaSenha !== ownPasswordForm.confirmarNovaSenha) {
      setPasswordError('A confirmacao da nova senha nao confere.')
      return
    }

    setSaving(true)

    try {
      await changeOwnPassword(ownPasswordForm.senhaAtual, ownPasswordForm.novaSenha)
      addLog('USUARIO', currentUser?.id ?? 'usuario-logado', 'EDITADO', 'Usuario alterou a propria senha')
      setOwnPasswordForm({ senhaAtual: '', novaSenha: '', confirmarNovaSenha: '' })
      setPasswordMessage('Senha alterada com sucesso.')
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Nao foi possivel alterar a senha.')
    } finally {
      setSaving(false)
    }
  }

  async function toggleStatus(user: AppUser) {
    const nextStatus = user.status === 'ATIVO' ? 'INATIVO' : 'ATIVO'
    setError('')
    setSaving(true)

    try {
      await setUserStatus(user.id, nextStatus)
      addLog('USUARIO', user.id, 'EDITADO', `Status alterado para ${nextStatus}`)
    } catch {
      setError('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  async function removeUser(user: AppUser) {
    setError('')

    if (usersPersistence !== 'lumi') {
      setError('Erro, tente novamente mais tarde.')
      return
    }

    if (
      currentUser?.id === user.id
      || currentUser?.email.trim().toLowerCase() === user.email.trim().toLowerCase()
    ) {
      setError('Nao e possivel excluir o usuario atualmente logado.')
      return
    }

    const activeAdmins = users.filter(item => item.perfil === 'ADMINISTRADOR' && item.status === 'ATIVO')
    if (user.perfil === 'ADMINISTRADOR' && user.status === 'ATIVO' && activeAdmins.length <= 1) {
      setError('O sistema precisa manter pelo menos um administrador ativo.')
      return
    }

    const confirmed = window.confirm(`Excluir o usuario ${user.nome}? Esta acao nao pode ser desfeita.`)
    if (!confirmed) return

    setSaving(true)

    try {
      await deleteUser(user.id)
      addLog('USUARIO', user.id, 'CANCELADO', `Usuario ${user.nome} excluido`)
      if (selectedId === user.id) setSelectedId(users.find(item => item.id !== user.id)?.id ?? '')
      if (editing?.id === user.id) resetForm()
    } catch {
      setError('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page-enter space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#f7f8f8]">Usuarios</h1>
          <p className="mt-1 text-[13px] text-[#62666d]">
            Controle de acesso compartilhado para validacao do nucleo financeiro.
          </p>
          <p className="mt-1 text-[12px] text-[#62666d]">
            Base: {usersPersistence === 'lumi' ? 'Compartilhada' : 'Acesso inicial'}
            {usersLoading ? ' carregando...' : ''}
          </p>
        </div>
        <div className="text-right text-[12px] text-[#62666d]">
          Usuario atual: <span className="text-[#d0d6e0]">{currentUser?.nome}</span>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Metric label="Usuarios" value={users.length} />
        <Metric label="Ativos" value={ativos} />
        <Metric label="Administradores" value={admins} />
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_360px]">
        <section className="card p-0">
          <div className="border-b border-[rgba(255,255,255,0.07)] px-4 py-3">
            <h2 className="text-[15px] font-medium text-[#f7f8f8]">Acessos cadastrados</h2>
            <p className="mt-1 text-[12px] text-[#62666d]">
              Clique em editar para alterar perfil, status ou redefinir a senha.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Perfil</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!usersLoading && users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-[#62666d]">
                      Nenhum usuario foi carregado.
                    </td>
                  </tr>
                )}
                {paginatedUsers.items.map(user => (
                  <tr key={user.id} className={selectedUser?.id === user.id ? 'bg-[rgba(240,64,59,0.08)]' : ''}>
                    <td className="font-medium text-[#f7f8f8]">{user.nome}</td>
                    <td>{user.email}</td>
                    <td>{user.perfil}</td>
                    <td>
                      <StatusBadge status={user.status} />
                    </td>
                    <td>
                      <div className="flex justify-end gap-2">
                        <button className="btn-secondary text-xs" onClick={() => startEdit(user)}>
                          Editar
                        </button>
                        <button className="btn-ghost text-xs" onClick={() => toggleStatus(user)}>
                          {user.status === 'ATIVO' ? 'Inativar' : 'Ativar'}
                        </button>
                        <button className="btn-ghost text-xs text-red-300" onClick={() => removeUser(user)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        <PaginationControls
          page={paginatedUsers.safePage}
          pageSize={pageSize}
          totalItems={users.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
          </div>
        </section>

        <section className="card p-4">
          <h2 className="text-[15px] font-medium text-[#f7f8f8]">
            {editing ? 'Editar usuario' : 'Novo usuario'}
          </h2>
          <form className="mt-4 space-y-3" onSubmit={submit}>
            <Field label="Nome">
              <input
                className="input-field"
                value={form.nome}
                onChange={event => setForm(prev => ({ ...prev, nome: event.target.value }))}
                placeholder="Nome completo"
              />
            </Field>
            <Field label="E-mail">
              <input
                className="input-field"
                type="email"
                value={form.email}
                onChange={event => setForm(prev => ({ ...prev, email: event.target.value }))}
                placeholder="usuario@4sengenharia.com.br"
              />
            </Field>
            <Field label="Perfil">
              <select
                className="input-field"
                value={form.perfil}
                onChange={event => setForm(prev => ({ ...prev, perfil: event.target.value as UserPerfil }))}
              >
                {perfis.map(perfil => (
                  <option key={perfil} value={perfil}>
                    {perfil}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                className="input-field"
                value={form.status}
                onChange={event => setForm(prev => ({ ...prev, status: event.target.value as AppUser['status'] }))}
              >
                <option value="ATIVO">ATIVO</option>
                <option value="INATIVO">INATIVO</option>
              </select>
            </Field>
            <Field label={editing ? 'Nova senha (opcional)' : 'Senha inicial'}>
              <input
                className="input-field"
                type="password"
                value={form.senha}
                onChange={event => setForm(prev => ({ ...prev, senha: event.target.value }))}
                placeholder="Minimo 6 caracteres"
                autoComplete="new-password"
              />
            </Field>
            <Field label="Confirmar senha">
              <input
                className="input-field"
                type="password"
                value={form.confirmarSenha}
                onChange={event => setForm(prev => ({ ...prev, confirmarSenha: event.target.value }))}
                placeholder="Repita a senha"
                autoComplete="new-password"
              />
            </Field>
            {error ? (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                {error}
              </div>
            ) : null}
            <button className="btn-primary w-full" type="submit" disabled={saving || usersLoading}>
              {saving ? 'Salvando...' : editing ? 'Salvar alteracoes' : 'Criar usuario'}
            </button>
            {editing ? (
              <button className="btn-secondary w-full" type="button" onClick={resetForm}>
                Cancelar edicao
              </button>
            ) : null}
          </form>

          <div className="my-5 border-t border-[rgba(255,255,255,0.07)]" />

          <h2 className="text-[15px] font-medium text-[#f7f8f8]">Minha senha</h2>
          <form className="mt-4 space-y-3" onSubmit={submitOwnPassword}>
            <Field label="Senha atual">
              <input
                className="input-field"
                type="password"
                value={ownPasswordForm.senhaAtual}
                onChange={event => setOwnPasswordForm(prev => ({ ...prev, senhaAtual: event.target.value }))}
                autoComplete="current-password"
              />
            </Field>
            <Field label="Nova senha">
              <input
                className="input-field"
                type="password"
                value={ownPasswordForm.novaSenha}
                onChange={event => setOwnPasswordForm(prev => ({ ...prev, novaSenha: event.target.value }))}
                autoComplete="new-password"
              />
            </Field>
            <Field label="Confirmar nova senha">
              <input
                className="input-field"
                type="password"
                value={ownPasswordForm.confirmarNovaSenha}
                onChange={event => setOwnPasswordForm(prev => ({ ...prev, confirmarNovaSenha: event.target.value }))}
                autoComplete="new-password"
              />
            </Field>
            {passwordError ? (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                {passwordError}
              </div>
            ) : null}
            {passwordMessage ? (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
                {passwordMessage}
              </div>
            ) : null}
            <button className="btn-secondary w-full" type="submit" disabled={saving || usersLoading}>
              Alterar minha senha
            </button>
          </form>
        </section>
      </div>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="card p-4">
      <p className="text-[12px] text-[#62666d]">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-[#f7f8f8]">{value}</p>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] text-[#8a8f98]">{label}</span>
      {children}
    </label>
  )
}
